import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';

interface Customer { id: string; name: string; }
interface Contact { id: string; name: string; role: string | null; customer: string | null; }

interface Props {
  onClose: () => void;
  initial?: {
    id?: string;
    code?: string;
    name?: string;
    customerId?: string | null;
    customerRef?: string | { id?: string; name?: string } | null;
    custRef?: string | null;
    status?: string;
    manager?: string | null;
    managerPhone?: string | null;
    supervisorId?: string | null;
    clientPmId?: string | null;
    clientPMId?: string | null;
    clientFmId?: string | null;
    clientFMId?: string | null;
    clientCmId?: string | null;
    clientCMId?: string | null;
    clsManagerId?: string | null;
    clsMgrId?: string | null;
    startDate?: string | null;
    endDate?: string | null;
    address?: string | null;
    postcode?: string | null;
  };
}

export function AddProjectModal({ onClose, initial }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;

  const [code, setCode] = useState(initial?.code ?? '');
  const [customerId, setCustomerId] = useState(initial?.customerId ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [customerRef, setCustomerRef] = useState(
    typeof initial?.custRef === 'string' ? initial.custRef
    : typeof initial?.customerRef === 'string' ? initial.customerRef
    : '',
  );
  const [status, setStatus] = useState(initial?.status ?? 'live');
  const [manager, setManager] = useState(initial?.manager ?? '');
  const [managerPhone, setManagerPhone] = useState(initial?.managerPhone ?? '');
  const [supervisorId, setSupervisorId] = useState(initial?.supervisorId ?? '');
  const [clientPmId, setClientPmId] = useState(initial?.clientPMId ?? initial?.clientPmId ?? '');
  const [clientFmId, setClientFmId] = useState(initial?.clientFMId ?? initial?.clientFmId ?? '');
  const [clientCmId, setClientCmId] = useState(initial?.clientCMId ?? initial?.clientCmId ?? '');
  const [clsManagerId, setClsManagerId] = useState(initial?.clsMgrId ?? initial?.clsManagerId ?? '');
  const [startDate, setStartDate] = useState(initial?.startDate?.slice(0, 10) ?? '');
  const [endDate, setEndDate] = useState(initial?.endDate?.slice(0, 10) ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [postcode, setPostcode] = useState(initial?.postcode ?? '');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: customers = [] } = useQuery({ queryKey: ['customers'], queryFn: () => api.get<Customer[]>('/customers').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });

  const save = useMutation({
    mutationFn: async () => {
      const body: any = {
        code: code.trim(),
        name: name.trim(),
        customerId: customerId || undefined,
        custRef: customerRef.trim() || null,
        status,
        manager: manager.trim() || null,
        managerPhone: managerPhone.trim() || null,
        supervisorId: supervisorId || null,
        clientPMId: clientPmId || null,
        clientFMId: clientFmId || null,
        clientCMId: clientCmId || null,
        clsMgrId: clsManagerId || null,
        startDate: startDate || null,
        endDate: endDate || null,
        address: address.trim() || undefined,
        postcode: postcode.trim() || undefined,
      };
      if (editing && initial?.id) return api.patch(`/projects/${initial.id}`, body);
      return api.post('/projects', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success(editing ? 'Project updated' : 'Project added', name);
      onClose();
    },
    onError: (e: any) => {
      toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again');
    },
  });

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" style={{ width: 620, maxWidth: '98vw' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-title" id="ap-title">{editing ? '✏ Edit Project' : '+ New Project'}</div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Project Code</label><input type="text" className="form-input" id="ap-id" value={code} onChange={(e) => setCode(e.target.value)} /></div>
          <div className="form-row">
            <label className="form-label">Customer</label>
            <SearchableSelect
              id="ap-customer"
              value={customerId ?? ''}
              onChange={(v) => setCustomerId(v)}
              placeholder="— Select customer —"
              options={[
                { value: '', label: '— Select customer —' },
                ...customers.map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          </div>
        </div>
        <div className="form-row"><label className="form-label">Project Name</label><input type="text" className="form-input" id="ap-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Customer Ref</label><input type="text" className="form-input" id="ap-custref" value={customerRef ?? ''} onChange={(e) => setCustomerRef(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Status</label>
            <SearchableSelect
              id="ap-status"
              value={status}
              onChange={(v) => setStatus(v)}
              placeholder="— Select status —"
              options={[
                { value: 'live', label: 'Live' },
                { value: 'completed', label: 'Completed' },
                { value: 'tender', label: 'Tender' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
          </div>
          <div className="form-row"><label className="form-label">Project Manager</label><input type="text" className="form-input" id="ap-manager" value={manager ?? ''} onChange={(e) => setManager(e.target.value)} /></div>
        </div>
        <div className="form-row"><label className="form-label">Manager Phone</label><input type="text" className="form-input" id="ap-manager-phone" placeholder="07..." value={managerPhone ?? ''} onChange={(e) => setManagerPhone(e.target.value)} /></div>

        <div style={{ marginTop: 6, padding: '10px 12px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10 }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 8 }}>Key Contacts</div>
          <div className="fg2">
            <div className="form-row">
              <label className="form-label">Site Supervisor <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— from contacts</span></label>
              <SearchableSelect
                id="ap-supervisor-id"
                value={supervisorId ?? ''}
                onChange={(v) => setSupervisorId(v)}
                placeholder="— None —"
                options={[
                  { value: '', label: '— None —' },
                  ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}` })),
                ]}
              />
            </div>
            <div className="form-row">
              <label className="form-label">Client PM <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— Client Project Manager</span></label>
              <SearchableSelect
                id="ap-client-pm-id"
                value={clientPmId ?? ''}
                onChange={(v) => setClientPmId(v)}
                placeholder="— None —"
                options={[
                  { value: '', label: '— None —' },
                  ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}` })),
                ]}
              />
            </div>
          </div>
          <div className="fg2">
            <div className="form-row">
              <label className="form-label">Client FM <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— Client Financial Manager</span></label>
              <SearchableSelect
                id="ap-client-fm-id"
                value={clientFmId ?? ''}
                onChange={(v) => setClientFmId(v)}
                placeholder="— None —"
                options={[
                  { value: '', label: '— None —' },
                  ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}` })),
                ]}
              />
            </div>
            <div className="form-row">
              <label className="form-label">Client CM <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— Client Construction Manager</span></label>
              <SearchableSelect
                id="ap-client-cm-id"
                value={clientCmId ?? ''}
                onChange={(v) => setClientCmId(v)}
                placeholder="— None —"
                options={[
                  { value: '', label: '— None —' },
                  ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}` })),
                ]}
              />
            </div>
          </div>
          <div className="fg2">
            <div className="form-row">
              <label className="form-label">CLS Manager <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— CLS Account Manager</span></label>
              <SearchableSelect
                id="ap-cls-manager-id"
                value={clsManagerId ?? ''}
                onChange={(v) => setClsManagerId(v)}
                placeholder="— None —"
                options={[
                  { value: '', label: '— None —' },
                  ...contacts.map((c) => ({ value: c.id, label: `${c.name}${c.role ? ` — ${c.role}` : ''}` })),
                ]}
              />
            </div>
            <div></div>
          </div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Start Date</label><input type="date" className="form-input" id="ap-start" value={startDate ?? ''} onChange={(e) => setStartDate(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">End Date</label><input type="date" className="form-input" id="ap-end" value={endDate ?? ''} onChange={(e) => setEndDate(e.target.value)} /></div>
        </div>
        <div className="fg2">
          <div className="form-row"><label className="form-label">Full Address</label><input type="text" className="form-input" id="ap-address" value={address ?? ''} onChange={(e) => setAddress(e.target.value)} /></div>
          <div className="form-row"><label className="form-label">Postcode</label><input type="text" className="form-input" id="ap-postcode" value={postcode ?? ''} onChange={(e) => setPostcode(e.target.value)} /></div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" id="ap-save-btn" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save Project'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
